<div class="dashboard-container">
  <div class="sticky-header">
    <h1 class="dashboard-title">Backoffice Dashboard</h1>
    <app-date-range-selector
      [selectedPeriod]="selectedPeriod()"
      [startDate]="startDate()"
      [endDate]="endDate()"
      [includeWeek]="true"
      (periodChange)="onPeriodChange($event)"
      (dateRangeChange)="onDateRangeChange($event)"
    />
  </div>
  @if (dashboardService.errorMessage(); as error) {
    <app-error-banner [message]="error" />
  }

  <div class="dashboard-grid">
    <div class="card-wrapper full-width">
      <p-card header="System Overview">
        @if (systemOverview().length === 0) {
          <div class="overview-grid">
            @for (item of [1, 2, 3, 4]; track item) {
              <div class="metric-card-wrapper">
                <p-card styleClass="perf-metric-card skeleton-card">
                  <p-skeleton width="60%" height="1rem" styleClass="mb-2"></p-skeleton>
                  <p-skeleton width="80%" height="2.5rem"></p-skeleton>
                </p-card>
              </div>
            }
          </div>
        } @else {
          <div class="overview-grid">
            @for (metric of systemOverview(); track metric.label) {
              <div class="metric-card-wrapper" [class.highlighted]="metric.highlighted">
                <p-card
                  [styleClass]="
                    metric.highlighted ? 'perf-metric-card highlighted-metric' : 'perf-metric-card'
                  "
                >
                  <div class="metric-header">
                    <span class="metric-label">{{ metric.label }}</span>
                    <div class="metric-trend-badge" [class]="getTrendColor(metric.trend)">
                      <i [class]="getTrendIcon(metric.trend)"></i>
                      <span>{{ metric.trend | number: '1.0-1' }}%</span>
                    </div>
                  </div>
                  <div class="metric-content">
                    <div class="metric-value">
                      {{ metric.value }}
                      <span class="metric-unit">{{ metric.unit }}</span>
                    </div>
                  </div>
                </p-card>
              </div>
            }
          </div>
        }
      </p-card>
    </div>

    <div class="card-wrapper">
      <p-card header="New Users Growth">
        @if (connectionState() === 'connecting') {
          <div class="p-4">
            <p-skeleton height="300px"></p-skeleton>
          </div>
        } @else {
          <div class="chart-container">
            <p-chart
              type="line"
              [data]="userActivityData()"
              [options]="activityChartOptions()"
            ></p-chart>
          </div>
        }
      </p-card>
    </div>

    <div class="card-wrapper">
      <p-card header="Session Activity">
        @if (connectionState() === 'connecting') {
          <div class="p-4">
            <p-skeleton height="300px"></p-skeleton>
          </div>
        } @else {
          <div class="chart-container">
            <p-chart
              type="line"
              [data]="sessionActivityData()"
              [options]="activityChartOptions()"
            ></p-chart>
          </div>
        }
      </p-card>
    </div>

    <div class="card-wrapper full-width">
      <p-card header="LLM Intelligence & Costs">
        @if (llmUsageMetrics().length === 0) {
          <div class="overview-grid llm-grid">
            @for (item of [1, 2, 3]; track item) {
              <div class="metric-card-wrapper">
                <p-card styleClass="perf-metric-card skeleton-card">
                  <p-skeleton width="60%" height="1rem" styleClass="mb-2"></p-skeleton>
                  <p-skeleton width="80%" height="2.5rem"></p-skeleton>
                </p-card>
              </div>
            }
          </div>
        } @else {
          <div class="overview-grid llm-grid">
            @for (metric of llmUsageMetrics(); track metric.label) {
              <div class="metric-card-wrapper">
                <p-card styleClass="perf-metric-card">
                  <div class="metric-header">
                    <span class="metric-label">{{ metric.label }}</span>
                    @if (metric.trend !== 0) {
                      <div class="metric-trend-badge" [class]="getTrendColor(metric.trend)">
                        <i [class]="getTrendIcon(metric.trend)"></i>
                        <span>{{ metric.trend | number: '1.0-1' }}%</span>
                      </div>
                    }
                  </div>
                  <div class="metric-content">
                    <div class="metric-value">
                      {{ metric.value }}
                      <span class="metric-unit">{{ metric.unit }}</span>
                    </div>
                  </div>
                </p-card>
              </div>
            }
          </div>
        }
      </p-card>
    </div>

    <div class="card-wrapper full-width">
      <p-card header="Sessions Count per Organisation">
        @if (topOrganisations().length === 0) {
          <div class="p-4">
            <p-skeleton height="150px"></p-skeleton>
          </div>
        } @else {
          <p-table [value]="topOrganisations()" styleClass="p-datatable-striped">
            <ng-template pTemplate="header">
              <tr>
                <th style="width: 30%">Organisation</th>
                <th style="width: 15%">Users</th>
                <th style="width: 15%">Practice</th>
                <th style="width: 15%">Live</th>
                <th style="width: 25%">Total Activity</th>
              </tr>
            </ng-template>
            <ng-template pTemplate="body" let-org>
              <tr>
                <td>
                  <a
                    class="link-cell"
                    tabindex="0"
                    role="link"
                    (click)="openOrg(org.orgId)"
                    (keydown.enter)="openOrg(org.orgId)"
                    >{{ org.name }}</a
                  >
                </td>
                <td><p-tag [value]="org.userCount" severity="info"></p-tag></td>
                <td class="text-color-secondary">{{ org.practiceSessions }}</td>
                <td class="text-color-secondary">{{ org.liveSessions }}</td>
                <td class="font-bold">{{ org.totalActivity }}</td>
              </tr>
            </ng-template>
          </p-table>
        }
      </p-card>
    </div>

    <div class="card-wrapper full-width">
      <p-card header="Activity Minutes per Organisation">
        @if (organisationActivityMinutes().length === 0) {
          <div class="p-4">
            <p-skeleton height="150px"></p-skeleton>
          </div>
        } @else {
          <p-table [value]="organisationActivityMinutes()" styleClass="p-datatable-striped">
            <ng-template pTemplate="header">
              <tr>
                <th style="width: 30%">Organisation</th>
                <th style="width: 20%">Practice Minutes</th>
                <th style="width: 20%">Live Minutes</th>
                <th style="width: 30%">Total Minutes</th>
              </tr>
            </ng-template>
            <ng-template pTemplate="body" let-org>
              <tr>
                <td>
                  <a
                    class="link-cell"
                    tabindex="0"
                    role="link"
                    (click)="openOrg(org.orgId)"
                    (keydown.enter)="openOrg(org.orgId)"
                    >{{ org.name }}</a
                  >
                </td>
                <td class="text-color-secondary">{{ org.practiceMinutes | number }}</td>
                <td class="text-color-secondary">{{ org.liveMinutes | number }}</td>
                <td class="font-bold">{{ org.totalMinutes | number }}</td>
              </tr>
            </ng-template>
          </p-table>
        }
      </p-card>
    </div>

    <div class="card-wrapper">
      <p-card header="Power Users (Practice)">
        @if (!topUsers()) {
          <div class="p-4">
            <p-skeleton height="200px"></p-skeleton>
          </div>
        } @else {
          <p-table [value]="topUsers()!.topByPractice" styleClass="p-datatable-sm">
            <ng-template pTemplate="header">
              <tr>
                <th>User</th>
                <th class="text-right">Sessions</th>
              </tr>
            </ng-template>
            <ng-template pTemplate="body" let-user>
              <tr>
                <td>
                  <a
                    class="link-cell"
                    tabindex="0"
                    role="link"
                    (click)="openUserSessions(user.userId)"
                    (keydown.enter)="openUserSessions(user.userId)"
                  >
                    {{ user.name || user.email }}
                  </a>
                  <div class="text-sm text-color-secondary">{{ user.organisation }}</div>
                </td>
                <td class="text-right font-bold">{{ user.count }}</td>
              </tr>
            </ng-template>
          </p-table>
        }
      </p-card>
    </div>

    <div class="card-wrapper">
      <p-card header="Power Users (Live Assistant)">
        @if (!topUsers()) {
          <div class="p-4">
            <p-skeleton height="200px"></p-skeleton>
          </div>
        } @else {
          <p-table [value]="topUsers()!.topByLive" styleClass="p-datatable-sm">
            <ng-template pTemplate="header">
              <tr>
                <th>User</th>
                <th class="text-right">Sessions</th>
              </tr>
            </ng-template>
            <ng-template pTemplate="body" let-user>
              <tr>
                <td>
                  <a
                    class="link-cell"
                    tabindex="0"
                    role="link"
                    (click)="openUserSessions(user.userId)"
                    (keydown.enter)="openUserSessions(user.userId)"
                  >
                    {{ user.name || user.email }}
                  </a>
                  <div class="text-sm text-color-secondary">{{ user.organisation }}</div>
                </td>
                <td class="text-right font-bold">{{ user.count }}</td>
              </tr>
            </ng-template>
          </p-table>
        }
      </p-card>
    </div>
  </div>
</div>
